import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useDatasets from "datasets/hooks/useDatasets";
import useStatistics from "datasets/hooks/useStatistics";
import Dataset from "datasets/types/interfaces/dataset";
import { formatDistanceToNowStrict } from "date-fns";
import { ArrowRight, Folder, Notebook, Plus, Search } from "lucide-react";
import pluralize from "pluralize";
import prettyBytes from "pretty-bytes";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFavorites } from "~/hooks/useFavorites";
import { useFetcher } from "~/hooks/useFetcher";
import useUserPermission from "~/hooks/useUserPermission";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import useConversations from "~/pages/admin/hooks/useConversations";
import { DividedList } from "~/pages/manifold-nextgen/components/DividedList";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { AccessLevelBadge } from "~/pages/manifold-nextgen/components/datasets/AccessLevelBadge";
import { CreateDatasetModal } from "~/pages/manifold-nextgen/components/datasets/CreateDatasetModal";
import { NewProjectDialog } from "~/pages/manifold-nextgen/components/projects/NewProjectDialog";
import { ProjectAccessBadge } from "~/pages/manifold-nextgen/components/projects/ProjectAccessBadge";
import { useCanUseAIAgents } from "~/pages/manifold-nextgen/hooks/useCanUseAIAgents";
import { nextgenConversationPath, nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import { nextGenDatasetDetailPath, ROUTE_NEXTGEN_DATASETS } from "~/router/constants";
import { AgentList, agentListPath } from "~/services/agents";
import { ProjectList, ProjectListItem, projectListPath } from "~/services/projects";

const PREVIEW_LIMIT = 5;
const RECENT_SESSIONS_LIMIT = 8;
const SEARCH_RESULT_LIMIT = 5;

// Stable fallbacks for while data is loading. A fresh `[]` literal on every
// render would change reference each time, cascading through the useMemos
// that build `heroContent` and re-triggering the header's setHeroContent
// effect every render (the effect's equality check can't treat the JSX's
// inline event handlers as equal across renders, so it always sees "new"
// content) — an infinite render loop.
const EMPTY_PROJECTS: ProjectListItem[] = [];
const EMPTY_DATASETS: Dataset[] = [];

const greetingForHour = (hour: number): string => {
  if (hour < 12) {
    return "Good morning";
  }
  if (hour < 18) {
    return "Good afternoon";
  }
  return "Good evening";
};

const matchesQuery = (haystack: string, query: string) => haystack.toLowerCase().includes(query);

const SeeAllLink = ({ to, count }: { to: string; count: number }) => (
  <Link
    to={to}
    className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
  >
    See all {count}
    <ArrowRight className="size-3" aria-hidden="true" />
  </Link>
);

interface HomeProjectRowProps {
  project: ProjectListItem;
  favorite: boolean;
  onToggleFavorite: () => void;
}

// Compact row for the Home preview: dataset/agent/updated-at summary in the
// subtext instead of avatar stacks, an owner chip (shown only when the
// caller owns the project) next to the favorite toggle. Home-specific — the
// shared ProjectCard's `view="list"` (used on the real Projects page) is
// untouched.
const HomeProjectRow = ({ project, favorite, onToggleFavorite }: HomeProjectRowProps) => {
  const navigate = useNavigate();
  const lastActivity = formatDistanceToNowStrict(new Date(project.last_activity_at), {
    addSuffix: true,
  });

  return (
    <div
      className="flex cursor-pointer items-center gap-2 px-4 py-3 transition-colors hover:bg-muted"
      onClick={() => navigate(nextgenProjectPath(project.short_id))}
    >
      <Notebook
        className="size-5 shrink-0 text-muted-foreground"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-semibold text-card-foreground">{project.name}</h3>
        <p className="truncate text-xs text-muted-foreground">
          {project.dataset_count} {pluralize("dataset", project.dataset_count)} ·{" "}
          {project.agents.length} {pluralize("agent", project.agents.length)} · {lastActivity}
        </p>
      </div>
      <ProjectAccessBadge accessLevel={project.access_level} hideDefault />
      <FavoriteButton favorite={favorite} onToggle={onToggleFavorite} />
    </div>
  );
};

interface HomeDatasetRowProps {
  dataset: Dataset;
  favorite: boolean;
  onToggleFavorite: () => void;
}

// Compact row for the Home preview: size/updated-at summary in the subtext
// instead of table/byte-size stat chips, an owner chip (shown only when the
// caller owns the dataset) next to the favorite toggle. Home-specific — the
// shared DatasetCard's `view="list"` (used on the real Datasets page) is
// untouched.
const HomeDatasetRow = ({ dataset, favorite, onToggleFavorite }: HomeDatasetRowProps) => {
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useStatistics(dataset.id);
  const updatedAt = formatDistanceToNowStrict(new Date(dataset.updated_at), { addSuffix: true });

  return (
    <div
      className="flex cursor-pointer items-center gap-2 px-4 py-3 transition-colors hover:bg-muted"
      onClick={() => navigate(nextGenDatasetDetailPath(dataset.id))}
    >
      <Folder
        className="size-5 shrink-0 text-muted-foreground"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-semibold text-card-foreground">{dataset.title}</h3>
        <p className="truncate text-xs text-muted-foreground">
          {statsLoading ? "…" : prettyBytes(stats?.table_total_bytes ?? 0)} · {updatedAt}
        </p>
      </div>
      <AccessLevelBadge accessLevel={dataset.access_level} hideDefault />
      <FavoriteButton favorite={favorite} onToggle={onToggleFavorite} />
    </div>
  );
};

interface SearchDropdownProps {
  query: string;
  projects: ProjectListItem[];
  datasets: Dataset[];
  onNavigate: () => void;
}

// Command-palette-style results under the search box: grouped by entity
// type, navigating away closes it via `onNavigate` clearing the query.
const SearchDropdown = ({ query, projects, datasets, onNavigate }: SearchDropdownProps) => {
  const hasResults = projects.length > 0 || datasets.length > 0;

  return (
    <div
      role="listbox"
      className="absolute top-[calc(100%+8px)] right-0 left-0 z-50 max-h-[340px] overflow-y-auto rounded-2xl border border-border bg-white text-left text-foreground shadow-2xl"
    >
      {!hasResults && (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          No matches for &quot;{query}&quot;
        </p>
      )}
      {projects.length > 0 && (
        <div className="p-2">
          <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Projects
          </div>
          {projects.map((project) => (
            <Link
              key={project.id}
              to={nextgenProjectPath(project.short_id)}
              onClick={onNavigate}
              className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted"
            >
              <Notebook
                className="size-5 shrink-0 text-muted-foreground"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">{project.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {project.dataset_count} {pluralize("dataset", project.dataset_count)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      {datasets.length > 0 && (
        <div className="p-2">
          <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Datasets
          </div>
          {datasets.map((dataset) => (
            <Link
              key={dataset.id}
              to={nextGenDatasetDetailPath(dataset.id)}
              onClick={onNavigate}
              className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted"
            >
              <Folder
                className="size-5 shrink-0 text-muted-foreground"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">
                  {dataset.title}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {dataset.table_count} {pluralize("table", dataset.table_count)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const Home = () => {
  useDocumentTitle("Home");
  const user = useUser();
  const [search, setSearch] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const query = search.trim().toLowerCase();
  const hasCreateDatasetPermission = useUserPermission("create:catalog-dataset");
  const canUseAIAgents = useCanUseAIAgents();

  const { data: projectList } = useFetcher<ProjectList>(projectListPath());
  const { data: datasets } = useDatasets();
  const { data: agentList } = useFetcher<AgentList>(agentListPath());
  const { data: recentConversations, isLoading: recentSessionsLoading } = useConversations({
    isAdmin: false,
    nextgenOnly: true,
    limit: RECENT_SESSIONS_LIMIT,
    sortKey: "updated_at",
    sortOrder: "desc",
  });

  const allProjects = projectList?.items ?? EMPTY_PROJECTS;
  const allDatasets = datasets ?? EMPTY_DATASETS;
  const agentsById = useMemo(
    () => Object.fromEntries((agentList?.items ?? []).map((agent) => [agent.id, agent])),
    [agentList]
  );

  const {
    favorites: favoriteProjects,
    others: otherProjects,
    toggleFavorite: toggleFavoriteProject,
    isFavorite: isFavoriteProject,
  } = useFavorites("project-favorites", allProjects, (p) => p.id);
  const {
    favorites: favoriteDatasets,
    others: otherDatasets,
    toggleFavorite: toggleFavoriteDataset,
    isFavorite: isFavoriteDataset,
  } = useFavorites("dataset-favorites", allDatasets, (d) => d.id);

  const previewProjects = useMemo(
    () => [...favoriteProjects, ...otherProjects].slice(0, PREVIEW_LIMIT),
    [favoriteProjects, otherProjects]
  );
  const previewDatasets = useMemo(
    () => [...favoriteDatasets, ...otherDatasets].slice(0, PREVIEW_LIMIT),
    [favoriteDatasets, otherDatasets]
  );

  const searchProjects = useMemo(() => {
    if (!query) {
      return [];
    }
    return allProjects.filter((p) => matchesQuery(p.name, query)).slice(0, SEARCH_RESULT_LIMIT);
  }, [allProjects, query]);

  const searchDatasets = useMemo(() => {
    if (!query) {
      return [];
    }
    return allDatasets
      .filter((d) => matchesQuery(d.title, query) || matchesQuery(d.description ?? "", query))
      .slice(0, SEARCH_RESULT_LIMIT);
  }, [allDatasets, query]);

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearch("");
  };

  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);
  const name = user?.given_name || user?.name || "there";

  const heroContent = useMemo(
    () => (
      <div className="mx-auto flex w-full max-w-[660px] flex-col items-center gap-1 px-4 pt-6 pb-12 text-center">
        <h1 className="text-2xl font-bold tracking-tight text-white">
          {greeting}, {name}.
        </h1>
        <p className="text-sm text-[#C4B5FD]">What are you working on today?</p>
        <div
          ref={searchContainerRef}
          className="relative mt-3 w-full"
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setIsSearchOpen(false);
            }
          }}
        >
          <Search
            className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            placeholder="Search projects and datasets..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsSearchOpen(e.target.value.trim().length > 0);
            }}
            onFocus={() => {
              if (search.trim()) {
                setIsSearchOpen(true);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setIsSearchOpen(false);
              }
            }}
            aria-label="Search projects and datasets"
            className="h-13 rounded-xl border-none bg-white pl-12 text-base text-foreground shadow-lg"
          />
          {isSearchOpen && query && (
            <SearchDropdown
              query={query}
              projects={searchProjects}
              datasets={searchDatasets}
              onNavigate={closeSearch}
            />
          )}
        </div>
      </div>
    ),
    [greeting, name, search, isSearchOpen, query, searchProjects, searchDatasets]
  );

  useNextGenHeader({ variant: "colored", heroContent });

  return (
    <div
      className="mx-auto flex w-full flex-col gap-9 px-4 py-6"
      style={{ maxWidth: NEXTGEN_MAX_CONTENT_WIDTH }}
    >
      <section className="flex flex-col gap-3">
        <h2 className="text-[17px] font-semibold text-foreground">Recent sessions</h2>
        {recentSessionsLoading ? (
          <div
            role="status"
            aria-label="Loading recent sessions"
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          >
            {Array.from({ length: RECENT_SESSIONS_LIMIT }).map((_, i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-xl border border-border bg-muted"
              />
            ))}
          </div>
        ) : recentConversations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent sessions yet</p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" role="list">
            {recentConversations.map((conversation) => (
              <li key={conversation.id}>
                <Link
                  to={nextgenConversationPath(conversation.projectId, conversation.id)}
                  className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-3 transition-colors hover:border-primary/40 hover:bg-muted"
                >
                  <span data-testid={`recent-session-avatar-${conversation.id}`}>
                    <AgentAvatar
                      agent={conversation.agentId ? agentsById[conversation.agentId] : undefined}
                      size={28}
                      aria-hidden
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {conversation.displayName}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {conversation.projectName}
                      {conversation.projectName && " · "}
                      {formatDistanceToNowStrict(new Date(conversation.updatedAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid gap-6 md:grid-cols-2">
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <h2 className="text-[17px] font-semibold text-foreground">Projects</h2>
              <SeeAllLink to="/nextgen/projects" count={allProjects.length} />
            </div>
            {canUseAIAgents && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="New project"
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => setIsNewProjectOpen(true)}
                  >
                    <Plus aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Create a project</TooltipContent>
              </Tooltip>
            )}
          </div>
          {previewProjects.length === 0 ? (
            <p className="text-sm text-muted-foreground">No projects yet</p>
          ) : (
            <DividedList
              items={previewProjects}
              className="overflow-hidden rounded-xl border border-border"
              renderItem={(project) => (
                <HomeProjectRow
                  project={project}
                  favorite={isFavoriteProject(project.id)}
                  onToggleFavorite={() => toggleFavoriteProject(project.id)}
                />
              )}
            />
          )}
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <h2 className="text-[17px] font-semibold text-foreground">Datasets</h2>
              <SeeAllLink to={ROUTE_NEXTGEN_DATASETS} count={allDatasets.length} />
            </div>
            {hasCreateDatasetPermission && (
              <Tooltip>
                <CreateDatasetModal
                  trigger={
                    <TooltipTrigger asChild>
                      <Button
                        aria-label="New dataset"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                      >
                        <Plus aria-hidden="true" />
                      </Button>
                    </TooltipTrigger>
                  }
                />
                <TooltipContent>Create a dataset</TooltipContent>
              </Tooltip>
            )}
          </div>
          {previewDatasets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No datasets yet</p>
          ) : (
            <DividedList
              items={previewDatasets}
              className="overflow-hidden rounded-xl border border-border"
              renderItem={(dataset) => (
                <HomeDatasetRow
                  dataset={dataset}
                  favorite={isFavoriteDataset(dataset.id)}
                  onToggleFavorite={() => toggleFavoriteDataset(dataset.id)}
                />
              )}
            />
          )}
        </section>
      </div>

      <NewProjectDialog open={isNewProjectOpen} onOpenChange={setIsNewProjectOpen} />
    </div>
  );
};
